import { z } from "zod";
import { CONVERSATION_KEY_SEPARATOR, conversationChannelSchema, conversationKeyPartSchema, } from "./conversation-rollup.js";
/**
 * What a conversation is CALLED, as opposed to what it IS.
 *
 * The distinction is the whole module. `conversation-rollup.ts` owns identity:
 * which window a message belongs to, what its document id is, and what digest
 * says whether it is current. Not one of those derivations may read anything
 * here, and a test pins that: a person renaming themselves on their phone must
 * not republish every window of their conversation, must not move a document
 * id, and must not make a rebuilt rollup look changed.
 *
 * So a name is a VIEW over provider facts, computed at read time from the rows
 * the reader is already authorized for, and never written down. Two readers with
 * different permissions can legitimately see the same conversation under
 * different names — each named from the messages they may actually open — and
 * neither name is stored anywhere for the other to inherit.
 *
 * THE MVP BOUNDARY RUNS THROUGH HERE. There is no semantic identity merge in
 * this product: nothing in this module compares two names for similarity, infers
 * a person from an address, or joins two chats because they look like the same
 * human. Every name it returns is a string the provider itself attached to that
 * exact chat, or an honest statement that the provider attached none.
 */
/**
 * The two shapes a conversation can have, once the provider's own chat type is
 * normalized.
 *
 * `direct` is a conversation with exactly one counterparty, which is the only
 * shape where a person's display name can name the ROOM without inventing
 * anything: the room is the person. `group` is everything else, and it is
 * deliberately a single bucket — a Teams `meeting` chat is many-party in exactly
 * the way a `group` is, and naming either after one of its speakers would assert
 * a relationship the provider never stated.
 */
export const conversationChatKinds = ["direct", "group"];
export const conversationChatKindSchema = z.enum(conversationChatKinds);
/**
 * The provider's own chat-type vocabularies, normalized.
 *
 * `null` in and `null` out: a chat whose type could not be read is not assumed
 * to be direct. Guessing `direct` would let one speaker's name become the whole
 * room's, which is the one failure this module exists to prevent, so an
 * unreadable type falls through to the identifier case instead.
 */
export function conversationChatKindOf(channel, providerChatType) {
    if (providerChatType === null)
        return null;
    if (channel === "whatsapp") {
        if (providerChatType === "individual")
            return "direct";
        if (providerChatType === "group")
            return "group";
        return null;
    }
    if (providerChatType === "oneOnOne")
        return "direct";
    if (providerChatType === "group" || providerChatType === "meeting") {
        return "group";
    }
    return null;
}
/**
 * Where a conversation's name came from — carried beside the name so no surface
 * has to guess how much the string is worth.
 *
 * `provider_display_name` is the only value that means "a person or a room is
 * named here". The other two are both statements of absence, and they are
 * separate because they are different absences: a group the provider never gave
 * a subject to is not the same fact as a chat whose shape could not even be
 * read.
 */
export const conversationNameSources = [
    "provider_display_name",
    "group_without_recorded_name",
    "chat_identifier",
];
export const conversationNameSourceSchema = z.enum(conversationNameSources);
/**
 * The name to use when the provider supplied several.
 *
 * People rename themselves, so a chat legitimately carries a different display
 * name on messages from March and from August. The rule is the LATEST one the
 * reader may see: the provider's most recent statement about what that party
 * calls itself. Ties break on the source item id, the same total order the
 * rollup composes windows in, so the answer never depends on which row the
 * database happened to return first.
 *
 * A row with no send instant loses to any row that has one — it cannot be placed
 * in time, and letting it win would make the name depend on an unordered field.
 * When every candidate is undated the id alone decides, which is still total and
 * still stable.
 */
export function selectConversationDisplayName(candidates) {
    let bestKey = "";
    let best = null;
    for (const candidate of candidates) {
        const name = candidate.displayName?.trim() ?? "";
        if (name.length === 0)
            continue;
        const key = `${candidate.sentAt ?? ""}${CONVERSATION_KEY_SEPARATOR}${candidate.sourceItemId}`;
        if (best === null || key > bestKey) {
            bestKey = key;
            best = name;
        }
    }
    return best;
}
const channelLabels = {
    whatsapp: "WhatsApp",
    teams: "Microsoft Teams",
};
/**
 * A display name as it may be shown: trimmed, bounded, and unable to impersonate
 * structure.
 *
 * The name is provider-supplied text that this product prints into Markdown
 * headings and into what a reader takes for a file name. Left alone, a push name
 * containing a newline would forge a second line, and one containing U+001F
 * would look like a field boundary in a key the reader can see. Folding every
 * whitespace and control run to one space removes both without discarding a
 * single readable character.
 */
function foldedDisplayName(value) {
    return (value
        // eslint-disable-next-line no-control-regex -- folding C0/C1 is the point
        .replace(/[\u0000-\u001f\u007f-\u009f]/gu, " ")
        .replace(/\s+/gu, " ")
        .trim()
        .slice(0, 512));
}
export const conversationNameInputSchema = z
    .object({
    channel: conversationChannelSchema,
    /** WhatsApp's chat JID or the Teams chat id — the room, never a person. */
    chatContainerId: conversationKeyPartSchema,
    chatKind: conversationChatKindSchema.nullable(),
    /**
     * The counterparty's provider-given display name, already selected by
     * {@link selectConversationDisplayName} from rows this reader may open.
     *
     * COUNTERPARTY, not sender. The linked account's own push name travels on
     * its own outbound messages, and a conversation named after the person
     * reading it is worse than one named after a number.
     */
    counterpartyDisplayName: z.string().max(512).nullable(),
    participantCount: z.number().int().nonnegative().nullable(),
})
    .strict();
/**
 * Name one conversation, from provider facts only.
 *
 * The four cases are the four the product actually has, and each one is a
 * different statement rather than a different formatting of the same one:
 *
 * 1. A direct chat whose counterparty the provider named → that name. The room
 *    is one person and the provider said who.
 * 2. A group → never a person's name, whatever names its messages carry. The
 *    honest thing a group can say about itself without a recorded subject is how
 *    many people are in it, and when even that is unknown it says so. Measured
 *    2026-08-08: no WhatsApp group subject reaches this pipeline at all, and
 *    Teams' `chat.topic` is dropped before `teams_message_metadata`, so this
 *    branch is where every group lands today.
 * 3. Anything else — a direct chat the provider never named, or a chat whose
 *    type could not be read → the identifier, stated as an identifier.
 *
 * Case 3 is not an error and must not read as one. A chat with no recorded name
 * is a chat with no recorded name; presenting the JID as if it were the answer
 * to "who is this" is the untruth this module replaces.
 */
export function conversationName(input) {
    const parsed = conversationNameInputSchema.parse(input);
    const channel = channelLabels[parsed.channel];
    const chatContainerId = parsed.chatContainerId;
    const name = parsed.counterpartyDisplayName === null
        ? ""
        : foldedDisplayName(parsed.counterpartyDisplayName);
    if (parsed.chatKind === "direct" && name.length > 0) {
        return { text: name, source: "provider_display_name", chatContainerId };
    }
    if (parsed.chatKind === "group") {
        return {
            text: parsed.participantCount === null
                ? `${channel} grubu · katılımcı sayısı bilinmiyor`
                : `${channel} grubu · ${parsed.participantCount} katılımcı`,
            source: "group_without_recorded_name",
            chatContainerId,
        };
    }
    return {
        text: `${channel} sohbeti · adı kayıtlı değil · ${chatContainerId}`,
        source: "chat_identifier",
        chatContainerId,
    };
}
/**
 * Keep two conversations that honestly share a name from reading as one.
 *
 * SEPARATE, NEVER MERGED. Two people can use the same push name, and one person
 * can appear on two numbers; both produce identical text, and neither is
 * evidence that the chats are the same conversation. This function therefore
 * changes only what is DISPLAYED — it does not, and cannot, join anything: the
 * identities stay whatever `conversation-rollup.ts` derived, and the only input
 * it has is a list of already-named conversations.
 *
 * The qualifier is the chat's own identifier rather than an ordinal or a hash.
 * An ordinal shifts the moment a third chat appears, so yesterday's "Zahide (2)"
 * is today's "Zahide (3)" and the reader learns to distrust it. A hash is stable
 * but tells the reader nothing — a disambiguator nobody can interpret does not
 * disambiguate. The identifier is stable, meaningful, and already visible to
 * this reader on both chats.
 *
 * Only actual collisions are qualified. A name that is unique among the
 * conversations this reader can see is left exactly as it was, so the common
 * case stays clean.
 */
export function disambiguateConversationNames(names) {
    const counts = new Map();
    for (const name of names) {
        counts.set(name.text, (counts.get(name.text) ?? 0) + 1);
    }
    return names.map((name) => (counts.get(name.text) ?? 0) > 1
        ? { ...name, text: `${name.text} · ${name.chatContainerId}` }
        : name);
}
//# sourceMappingURL=conversation-name.js.map